import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LICENSE_TIERS = {
  basic: {
    name: "Basic Lease",
    price: 29.99,
    features: [
      "MP3 file download",
      "2,500 streaming plays",
      "Non-exclusive license",
      "Producer keeps 20% publishing royalties",
      "Credit required",
    ],
  },
  premium: {
    name: "Premium Lease",
    price: 59.99,
    features: [
      "MP3 + WAV file download",
      "10,000 streaming plays",
      "Non-exclusive license",
      "Radio broadcasting rights",
      "Producer keeps 20% publishing royalties",
      "Credit required",
    ],
    popular: true,
  },
  exclusive: {
    name: "Exclusive Rights",
    price: 500,
    features: [
      "Full ownership transfer",
      "Unlimited streaming plays",
      "Beat removed from store",
      "No credit required",
      "Full commercial rights",
    ],
  },
};

export default function LicenseModal({ beat, open, onClose }) {
  const [selectedLicense, setSelectedLicense] = useState("premium");
  const [loading, setLoading] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const discount = beat?.discount_percent || 0;
  const disc = (p) => Math.round(p * (1 - discount / 100) * 100) / 100;

  const handleCheckout = async () => {
    if (!beat) return;
    if (!customerEmail.trim()) {
      toast.error("Please enter your email for the license agreement.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/checkout/create-session`, {
        beat_id: beat.id,
        license_type: selectedLicense,
        origin_url: window.location.origin,
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to create checkout session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-background border-border" data-testid="license-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Choose License for "{beat?.title}"
          </DialogTitle>
        </DialogHeader>

        {discount > 0 && (
          <div className="mt-2 rounded-xl bg-primary/15 border border-primary/30 px-4 py-2 text-sm text-primary font-semibold" data-testid="discount-banner">
            🔥 {discount}% OFF — discount applied to all licenses below!
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {Object.entries(LICENSE_TIERS).map(([key, tier]) => (
            <motion.div
              key={key}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedLicense(key)}
              className={`license-card relative cursor-pointer rounded-2xl p-6 border-2 transition-all ${
                selectedLicense === key
                  ? "border-primary bg-primary/10"
                  : "border-border bg-card hover:border-white/20"
              } ${tier.popular ? "featured" : ""}`}
              data-testid={`license-option-${key}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                    POPULAR
                  </span>
                </div>
              )}

              <h3 className="font-heading font-bold text-lg mt-2">{tier.name}</h3>
              {discount > 0 ? (
                <div className="mt-2">
                  <span className="text-lg font-heading text-muted-foreground line-through mr-2">
                    ${tier.price}
                  </span>
                  <span className="text-3xl font-heading font-black text-primary">
                    ${disc(tier.price)}
                  </span>
                </div>
              ) : (
                <p className="text-3xl font-heading font-black text-primary mt-2">
                  ${tier.price}
                </p>
              )}

              <ul className="mt-4 space-y-2">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {selectedLicense === key && (
                <motion.div
                  layoutId="selected-indicator"
                  className="absolute top-4 right-4 w-6 h-6 rounded-full bg-primary flex items-center justify-center"
                >
                  <Check className="w-4 h-4 text-white" />
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="space-y-2">
            <Label htmlFor="buyer-name">Your Name</Label>
            <Input
              id="buyer-name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Jane Doe"
              className="rounded-xl bg-card border-border"
              data-testid="buyer-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="buyer-email">Email (for your license)</Label>
            <Input
              id="buyer-email"
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="you@email.com"
              className="rounded-xl bg-card border-border"
              data-testid="buyer-email-input"
            />
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-full px-6"
            data-testid="cancel-license-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCheckout}
            disabled={loading}
            className="rounded-full px-8 bg-primary hover:bg-primary/90 neon-glow"
            data-testid="checkout-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>Purchase ${discount > 0 ? disc(LICENSE_TIERS[selectedLicense].price) : LICENSE_TIERS[selectedLicense].price}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
